import { Alert, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TppCardSummary } from '../../../services/transitPassPool.service';
import { isTppBorrowable, isTppNeedsChargeFirst } from './tppCardPredicates';

type Props = {
  selected: TppCardSummary | null;
  otherCheckedOut: TppCardSummary[];
  /** プール全体の「選択以外の貸出中」枚数（ページ外含む） */
  otherCheckedOutTotal: number;
  money: (n: number) => string;
  zeroBalanceLabel: string;
  /** 履歴ゼロ時はカード行のチャージに任せ、バナーは出さない（重複 CTA 禁止） */
  selectedHasHistory: boolean;
  onReturn: (card: TppCardSummary) => void;
  onCharge: (card: TppCardSummary) => void;
};

const BANNER_CLASS =
  'mb-3 d-flex flex-wrap align-items-center justify-content-between gap-2 py-2';

/** 一覧下の次アクション案内 — 同時は1枚。選択中カードの操作を他カードより優先 */
export default function TppNextActionBanners({
  selected,
  otherCheckedOut,
  otherCheckedOutTotal,
  money,
  zeroBalanceLabel,
  selectedHasHistory,
  onReturn,
  onCharge,
}: Props) {
  const { t } = useTranslation();
  const other = otherCheckedOut[0];
  const othersCount = Math.max(otherCheckedOutTotal, otherCheckedOut.length);

  const showSelectedReturn = !!selected?.isCheckedOut;
  const needChargeOnReturn = !!selected && selected.balanceNumber <= 0;
  const showChargeBanner =
    !!selected &&
    !showSelectedReturn &&
    selectedHasHistory &&
    isTppNeedsChargeFirst(selected);
  const showOtherCheckedOut =
    !!selected &&
    !showSelectedReturn &&
    !showChargeBanner &&
    othersCount > 0 &&
    !isTppBorrowable(selected);

  return (
    <>
      {showSelectedReturn && selected && (
        <Alert variant="warning" className={BANNER_CLASS}>
          <div className="mb-0">
            <strong className="me-1">
              {t('tpp.next_return_title', { n: selected.displayNumber })}
            </strong>
            <span className="small d-block mt-1">
              {needChargeOnReturn
                ? t('tpp.next_return_body_need_charge', {
                    balance: money(selected.balanceNumber),
                  })
                : t('tpp.next_return_body')}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="warning"
              className="text-nowrap"
              onClick={() => onReturn(selected)}
            >
              {t('tpp.next_return_cta')}
            </Button>
            {needChargeOnReturn ? (
              <Button
                size="sm"
                variant="outline-success"
                className="text-nowrap"
                onClick={() => onCharge(selected)}
              >
                {t('tpp.next_charge_cta')}
              </Button>
            ) : null}
          </div>
        </Alert>
      )}

      {showOtherCheckedOut && (
        <Alert variant="warning" className={BANNER_CLASS}>
          <p className="mb-0 small">
            <strong className="me-1">
              {othersCount === 1 && other
                ? t('tpp.other_checked_out_one', { n: other.displayNumber })
                : t('tpp.other_checked_out_many', {
                    count: othersCount,
                    n: other?.displayNumber ?? '—',
                  })}
            </strong>
            <span className="text-muted">{t('tpp.other_checked_out_body_short')}</span>
          </p>
          {other && (
            <Button
              size="sm"
              variant="outline-warning"
              className="text-nowrap"
              onClick={() => onReturn(other)}
            >
              {t('tpp.other_checked_out_cta_short', { n: other.displayNumber })}
            </Button>
          )}
        </Alert>
      )}

      {showChargeBanner && selected && (
        <Alert
          variant={selected.balanceNumber < 0 ? 'warning' : 'info'}
          className={BANNER_CLASS}
        >
          <div className="mb-0">
            <strong className="me-1">
              {selected.balanceNumber < 0
                ? t('tpp.next_charge_title_negative', {
                    n: selected.displayNumber,
                    balance: money(selected.balanceNumber),
                  })
                : t('tpp.next_charge_title', {
                    n: selected.displayNumber,
                    balance: zeroBalanceLabel,
                  })}
            </strong>
            <span className="small d-block mt-1">
              {selected.balanceNumber < 0
                ? t('tpp.next_charge_body_negative')
                : t('tpp.next_charge_body')}
            </span>
          </div>
          <Button
            size="sm"
            variant="outline-success"
            className="text-nowrap"
            onClick={() => onCharge(selected)}
          >
            {t('tpp.next_charge_cta')}
          </Button>
        </Alert>
      )}
    </>
  );
}
